import { useCallback, useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import type { AppDispatch, RootState } from '@/redux/store'
import { setRentalDates, clearRentalDates, markDatesSentToCart } from '@/redux/slices/rentalDates'
import { cartApi, useGetCartQuery } from '@/redux/api/cartApi'
import { dayjsLA } from '@gearghost/date-utils'
import useIsAuthenticated from './useAuth'

const GUEST_CART_KEY = 'gearghost_guest_cart'

/**
 * Dates stored in the rental window. YYYY-MM-DD strings in the app's LA
 * timezone, matching the rentalDates slice and the cart schema's
 * ApiDateString. Nullable when no rental window has been picked yet.
 */
type RentalDate = string | null

/**
 * A persisted rental window is stale once its pickup day is before today
 * (LA). This happens when a user picks dates, leaves the page open, and the
 * day rolls over — the stored pickup is now in the past. Such a window can't
 * be booked (the calendar disables past days), and silently rehydrating it
 * produced the "past date selected / calendar fights re-selection" bug. We
 * treat a stale window as no window: clear it so the user picks fresh.
 */
export const isPickupInPast = (pickupDate: RentalDate): boolean => {
  if (!pickupDate) return false
  return dayjsLA(pickupDate).startOf('day').isBefore(dayjsLA().startOf('day'))
}

/** A window as one comparable string; no window at all is `null|null`. */
const windowKey = (pickupDate: RentalDate, returnDate: RentalDate): string => `${pickupDate}|${returnDate}`
const NO_WINDOW = windowKey(null, null)

/** The one mutation entry every send of the window shares, however many copies of the hook send. */
const CART_DATES_SEND = 'rental-dates-to-cart'

/**
 * Send a window to the signed-in renter's cart and record it as sent: a
 * person's own pick or clear, which goes out every time.
 */
const sendDatesToCart = (pickupDate: RentalDate, returnDate: RentalDate) => (dispatch: AppDispatch) => {
  dispatch(markDatesSentToCart(windowKey(pickupDate, returnDate)))
  return dispatch(cartApi.endpoints.updateCartDates.initiate({ pickupDate, returnDate }, { fixedCacheKey: CART_DATES_SEND }))
}

/**
 * Send a window unless it is the one last sent: the work every copy of this
 * hook runs (the header's and the page's) comes here. A thunk reads the
 * store as it is, so the second copy in one render finds the first one's
 * send recorded and sends nothing; the copies' own refs once let each send
 * it, and a renter with no cart got two saves at once (2026-10-19).
 */
const sendDatesToCartOnce = (pickupDate: RentalDate, returnDate: RentalDate) => (dispatch: AppDispatch, getState: () => RootState) => {
  if (getState().rentalDates.sentToCart === windowKey(pickupDate, returnDate)) return
  dispatch(sendDatesToCart(pickupDate, returnDate))
}

type GuestDates = {
  pickupDate: RentalDate
  returnDate: RentalDate
}

/**
 * Read guest cart dates from localStorage (no React state — just a snapshot).
 * Parse failures or missing keys degrade to a null pair rather than throwing.
 */
const readGuestDates = (): GuestDates => {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY)
    if (!stored) return { pickupDate: null, returnDate: null }
    const parsed = JSON.parse(stored)
    return {
      pickupDate: parsed?.pickupDate || null,
      returnDate: parsed?.returnDate || null,
    }
  } catch {
    return { pickupDate: null, returnDate: null }
  }
}

/**
 * Write dates into the existing guest cart in localStorage (merge, don't overwrite items).
 */
const writeGuestDates = (pickupDate: RentalDate, returnDate: RentalDate): void => {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY)
    const cart = stored ? JSON.parse(stored) : { listings: [] }
    cart.pickupDate = pickupDate
    cart.returnDate = returnDate
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart))
  } catch {
    // localStorage unavailable — silent fail
  }
}

/**
 * Single source of truth for rental dates across all pages.
 *
 * Reads from Redux slice (instant). Writes to Redux slice AND persists
 * to the cart API (logged in) or guest cart localStorage (guest).
 *
 * On mount / login, seeds the Redux slice from persisted storage if empty.
 * Signed in, it then keeps the cart on the window on screen: a window the
 * cart lacks is sent to it, and a cart whose dates are cleared elsewhere
 * clears the screen, as the app's useRentalDates does.
 */
const useRentalDates = () => {
  const dispatch = useAppDispatch()
  const isLoggedIn = useIsAuthenticated()
  const startDate = useAppSelector((state) => state.rentalDates.startDate)
  const endDate = useAppSelector((state) => state.rentalDates.endDate)

  // Cart API for persistence (skip query if not logged in)
  const { data: cartData } = useGetCartQuery(undefined, { skip: !isLoggedIn })

  // Seed Redux slice from persisted storage on mount / login.
  // Only runs once per session (or once after login when cartData arrives).
  const hasSeeded = useRef(false)
  useEffect(() => {
    if (hasSeeded.current) return
    const sliceEmpty = !startDate && !endDate

    if (isLoggedIn && cartData) {
      // Logged in and cart data loaded — seed from DB if slice is empty
      if (sliceEmpty && cartData.pickupDate && cartData.returnDate) {
        if (isPickupInPast(cartData.pickupDate)) {
          // Stale stored window — drop it (and clear the cart copy) so the
          // user isn't seeded back into an unbookable past pickup.
          dispatch(sendDatesToCartOnce(null, null))
        } else {
          dispatch(setRentalDates({
            startDate: cartData.pickupDate,
            endDate: cartData.returnDate,
          }))
        }
      }
      hasSeeded.current = true
    } else if (!isLoggedIn) {
      // Guest — seed from localStorage if slice is empty
      if (sliceEmpty) {
        const guest = readGuestDates()
        if (guest.pickupDate && guest.returnDate) {
          if (isPickupInPast(guest.pickupDate)) {
            // Stale stored window — drop it from localStorage too.
            writeGuestDates(null, null)
          } else {
            dispatch(setRentalDates({
              startDate: guest.pickupDate,
              endDate: guest.returnDate,
            }))
          }
        }
      }
      hasSeeded.current = true
    }
  }, [isLoggedIn, cartData, startDate, endDate, dispatch])

  // The screen and the cart keep one window, as the app's useRentalDates
  // does (Tyler, 2026-10-06: the cart page showed Oct 9 to 12 and priced
  // nothing, because the cart on the server had no dates). The cart prices
  // from its own copy, so the window on screen goes to it whenever the cart
  // holds another or none. The window last sent is the slice's `sentToCart`,
  // so a refetch still holding the old dates sends nothing more, and neither
  // does a second copy of this hook; `prevCartWindow` is the cart's last-seen
  // window, for the clear below.
  const prevCartWindow = useRef<string | null>(null)

  // A past window is the stale guard's to clear, never sent.
  useEffect(() => {
    if (!isLoggedIn || !cartData || !startDate || !endDate) return
    if (isPickupInPast(startDate)) return
    const shown = windowKey(startDate, endDate)
    if (windowKey(cartData.pickupDate, cartData.returnDate) === shown) {
      dispatch(markDatesSentToCart(shown))
      return
    }
    dispatch(sendDatesToCartOnce(startDate, endDate))
  }, [isLoggedIn, cartData, startDate, endDate, dispatch])

  // A cart whose dates went from set to none (a checkout, a clear on another
  // device) clears the screen too. Only on that change, never for a cart
  // that never had dates, and only ever clearing, so it cannot loop with the
  // effect above, which only ever sets the cart's dates.
  useEffect(() => {
    if (!isLoggedIn || !cartData) return
    const cartWindow = windowKey(cartData.pickupDate, cartData.returnDate)
    const previous = prevCartWindow.current
    prevCartWindow.current = cartWindow
    const cartWasCleared = previous !== null && previous !== NO_WINDOW && cartWindow === NO_WINDOW
    if (cartWasCleared && (startDate || endDate)) {
      dispatch(clearRentalDates())
    }
  }, [isLoggedIn, cartData, startDate, endDate, dispatch])

  const setDates = useCallback((start: RentalDate, end: RentalDate) => {
    // Instant UI update
    dispatch(setRentalDates({ startDate: start, endDate: end }))

    // Persist (fire-and-forget). Recorded as sent, so the window effect
    // above, in this copy or another, does not send it again.
    if (isLoggedIn) {
      dispatch(sendDatesToCart(start, end))
    } else {
      writeGuestDates(start, end)
    }
  }, [dispatch, isLoggedIn])

  const clearDates = useCallback(() => {
    dispatch(clearRentalDates())

    if (isLoggedIn) {
      dispatch(sendDatesToCart(null, null))
    } else {
      writeGuestDates(null, null)
    }
  }, [dispatch, isLoggedIn])

  // Invalidate a stale window already in the slice. The seeding effect above
  // only guards the empty-slice case (first hydrate). But the slice can also
  // hold a stale window directly — redux-persist rehydrating yesterday's
  // selection, or dates set earlier in a session that crossed midnight. This
  // always-runs guard catches that: whenever the current pickup is in the
  // past, clear the window (slice + persisted copy) so it can't drive an
  // unbookable past selection. Clearing sets the slice null (not past), so it
  // settles in one pass rather than looping. Every copy of the hook runs it,
  // so the cart's clear goes out once between them.
  useEffect(() => {
    if (!isPickupInPast(startDate)) return
    dispatch(clearRentalDates())
    if (isLoggedIn) {
      dispatch(sendDatesToCartOnce(null, null))
    } else {
      writeGuestDates(null, null)
    }
  }, [startDate, dispatch, isLoggedIn])

  // Dev-only console handle for reproducing the stale-window bug: persist a
  // rental window (e.g. a past pickup) then reload to watch rehydration. Gated
  // on import.meta.env.DEV so Vite tree-shakes it out of production builds.
  //   window.__ggSetRentalDates('2026-06-16', '2026-06-19')   // then reload
  useEffect(() => {
    if (!import.meta.env.DEV) return
    ;(window as unknown as Record<string, unknown>).__ggSetRentalDates = setDates
    return () => {
      delete (window as unknown as Record<string, unknown>).__ggSetRentalDates
    }
  }, [setDates])

  return { startDate, endDate, setDates, clearDates }
}

export default useRentalDates
